import { useEffect, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { bentukBahan, refBawaan } from '../../../shared/lib/referensi'
import PanelGambar, { bentukUbah, catatHasil } from './PanelGambar'
import {
  useBuatGambar, useBuatKonten, useBuatPromptGambar, useTulisIsiKonten, useBuatVideo,
  useReferensi, useTujuan, useUbahGambar, useUnggahGambarKonten,
} from '../../../domains/content/api'
import { useModelTenant } from '../../../domains/tenancy/api'
import {
  Alur, AreaTeks, BarisBuat, Bendera, Body, Isian, KV, KVBaris, Kolom2,
  Panel, Seg, Sub, Tombol, TombolBuat,
} from '../../../shared/ui'

const cx = (...a) => a.filter(Boolean).join(' ')

const KOSONG = {
  judul: '', jenis: 'photo', caption: '', pesan_kunci: '', hashtag: '',
  tayang_pada: '', tujuan: ['instagram'],
}

/**
 * BUAT KONTEN — mengikuti `arsip/docs-v2/desain/tenant-marketing.html`, panel `data-panel="buat"`.
 *
 * BENTUKNYA IKUT DESAIN, BUKAN KIRA-KIRA. Dua versi sebelumnya menyusun ulang tata letaknya
 * sendiri — tiga panel bertumpuk ke bawah, label di atas isian, <select> untuk pilihan yang
 * cuma dua. Desainnya menyusun "Isi konten" dan "Gambar" BERDAMPINGAN, dengan baris
 * label/nilai dan kontrol bersegmen. Bedanya bukan selera:
 *
 *   berdampingan   caption dan prompt gambar ditulis SALING MELIHAT — prompt yang bagus
 *                  lahir dari caption yang sudah ada di layar, bukan dari menggulung
 *   label/nilai    150px tetap di kiri membuat lima isian punya tepi yang sama; label di
 *                  atas isian menggandakan tinggi formulir tanpa menambah apa pun
 *   bersegmen      pilihan yang MENAGIH BIAYA tidak disembunyikan di balik satu klik
 *
 * SUMBER DAFTAR MODEL: `/dashboard/model` — yang DIPILIH admin tenant (tingkat 2), bukan
 * katalog penuh `/dashboard/model/tersedia`. Versi sebelumnya memakai katalog dan salah dua
 * kali: rutenya `tenant_admin` sehingga marketing kena 403, dan andai 403-nya dibuka ia
 * menawarkan model yang `image.py::_model_dipilih` tolak 422. Yang ditawarkan dan yang
 * boleh dipakai HARUS satu tabel: `tenant_ai_models`.
 *
 * EMPAT hal di desain tetap tidak dibangun, dan ditulis di layar sebagai belum ada:
 * referensi gambar (masukan, butuh penyimpanan berkas), collase (butuh urutan editorial +
 * jalur terbit carousel), harga per model SEBELUM memilih (`/dashboard/model` sengaja tidak
 * membawa tarif), dan pagu terpakai per tombol. Tombol yang ada tapi tidak bekerja lebih
 * buruk daripada tombol yang belum ada.
 */
/**
 * Model mana yang boleh dipilih untuk jenis konten ini.
 *
 * SEBELUM INI DISARING `role === 'image'` TANPA SYARAT, jadi tenant yang sudah memilih
 * Veo dan Seedance tetap tidak melihat satu pun model video: `/dashboard/model`
 * mengembalikannya, layarnya yang membuangnya. Operator melaporkannya persis — "di buat
 * konten saya belum bisa melihat model video".
 *
 * DIPISAH JADI FUNGSI MURNI supaya bisa diuji: jenisnya state yang cuma berubah karena
 * klik, dan `renderToString` tidak mengklik apa pun (XXIII-1) — uji render selamanya
 * melihat jalur foto, jadi jalur videonya lolos sepenuhnya.
 *
 * Peran lain (`text`, `chat`) TIDAK PERNAH masuk: keduanya menulis caption, bukan aset,
 * dan menawarkannya di sini berarti menagihkan model teks untuk pekerjaan gambar.
 */
export function modelUntukJenis(semua, jenis) {
  const peran = jenis === 'video' ? 'video' : 'image'
  return (semua ?? []).filter((m) => m?.role === peran)
}

export default function BuatKonten({ bahan = null, bersihkanBahan }) {
  const { t } = useBahasa()
  const buat = useBuatKonten()
  const isi = useTulisIsiKonten()
  const promptGambar = useBuatPromptGambar()
  // BRIEF untuk penulis prompt gambar (ADR-077). Boleh kosong, tidak diingat antar-konten.
  const [brief, setBrief] = useState('')
  // Bahan yang dipilih orangnya TIDAK ikut menentukan hasil, karena model teksnya tidak
  // menerima gambar. Ditampilkan sebagai peringatan — hasilnya berbeda, dan yang memilih
  // bahannya berhak tahu (ADR-077).
  const [bahanDiabaikan, setBahanDiabaikan] = useState(false)
  // Apa yang BENAR-BENAR dikirim ke model penulis prompt, dari panggilan terakhir.
  const [dikirimPrompt, setDikirimPrompt] = useState(null)
  // Pilihan mana yang sedang dipakai, supaya kartunya bisa ditandai. `-1` = belum memilih
  // (caption diisi tangan, atau dari "Buat dari judul").
  const [pilihDipakai, setPilihDipakai] = useState(-1)
  // Apa yang BENAR-BENAR dikirim ke model penulis prompt, dari panggilan
  // terakhir. Ditampilkan terlipat di panel Gambar.
  const gambar = useBuatGambar()
  const klipVideo = useBuatVideo()
  const katalogModel = useModelTenant()
  // Bahan dari tab sebelah mengisi JUDUL dan PROMPT GAMBAR, bukan caption.
  //
  // Judul berita bukan caption — ia ringkasan redaksi, bukan kalimat yang menyapa
  // pembaca. Menaruhnya di caption membuat orang menekan "simpan" dengan teks yang
  // terbaca seperti kutipan berita. Yang benar: judulnya jadi bahan, dan caption
  // dibuat dari situ lewat tombol "Buatkan dari judul".
  const [f, setF] = useState(() => (bahan ? { ...KOSONG, judul: bahan.title || '' } : KOSONG))
  const [jadi, setJadi] = useState(null)
  const [pilihanCaption, setPilihanCaption] = useState([])
  // SATU SLIDE = konten tunggal. Collase cuma daftar yang lebih panjang — bentuk yang
  // sama, jadi tidak ada dua jalur yang harus dijaga tetap sepadan.
  const [slide, setSlide] = useState(() => [{
    // Bahan dari tab sebelah menyemai PROMPT SLIDE PERTAMA, bukan caption. Judul berita
    // ringkasan redaksi, bukan kalimat yang menyapa pembaca; yang berguna darinya adalah
    // ALASAN mesin memilihnya, dan itu bahan yang bagus untuk gambar.
    // KOSONG, dan itu perbaikan.
    //
    // Bentuk lama menyemainya dengan `Ilustrasi untuk: <judul>. Sudut yang ditekankan:
    // <score_reason>`. Idenya masuk akal — alasan mesin memilih berita itu memang bahan
    // yang bagus. Yang tidak diperiksa: `score_reason` BUKAN kalimat deskriptif. Ia
    // potongan yang digabung titik koma, termasuk metadata murni seperti
    // "terbit <24 jam" dan "sudut: biaya, ojk".
    //
    // Model gambar membaca prompt HARFIAH, jadi ia mencoba menggambar "terbit <24 jam".
    // Terbukti di data dev: satu campaign menyimpan persis kalimat itu sebagai
    // image_prompt-nya.
    //
    // Tombol "Buatkan prompt dari caption" yang sudah ada di panel mengisinya dengan
    // benar — lewat model, dengan aturan image_prompt yang sama. Alasan skornya tetap
    // ditampilkan sebagai catatan di layar; ia cuma tidak lagi bocor ke prompt.
    prompt: '',
    url: '',
    biaya: null,
  }])
  const [modelPilihan, setModelPilihan] = useState('')
  const [refDipakai, setRefDipakai] = useState([])
  const [refDisemai, setRefDisemai] = useState(false)
  // Gambar temporary: peta `temp:<n>` -> data URI. Tidak pernah tersimpan,
  // hilang saat halaman ditutup.
  const [temp, setTemp] = useState({})
  const referensi = useReferensi()

  // REFERENSI BRAND TERPILIH SEJAK LAYAR DIBUKA.
  //
  // Gaya brand adalah hal yang diinginkan hampir selalu, dan yang hampir selalu
  // diinginkan tidak pantas jadi langkah manual yang harus diingat tiap kali. Sebelum
  // ini daftarnya mulai KOSONG, jadi tiap gambar yang dibuat tanpa mengklik referensi
  // lahir tanpa gaya brand sama sekali — tanpa satu pun tanda bahwa ada yang terlewat.
  //
  // Lewat efek, bukan nilai awal `useState`: daftarnya datang dari jaringan dan belum
  // ada saat render pertama. `refDisemai` menjaga ia terjadi SEKALI — tanpa itu, tiap
  // penyegaran daftar akan menimpa pilihan orang.
  useEffect(() => {
    if (refDisemai || !referensi.data) return
    setRefDisemai(true)
    const bawaan = refBawaan(referensi.data)
    if (bawaan.length) setRefDipakai(bawaan)
  }, [referensi.data, refDisemai])

  const tujuan = useTujuan()
  const ubahGbr = useUbahGambar()
  // Unggah gambar JADI — berbeda dari `unggah` di atas yang mengurus referensi.
  const unggahGambar = useUnggahGambarKonten()

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }))
  const bisa = f.judul.trim() && f.caption.trim() && f.tujuan.length

  const semua = katalogModel.data ?? []
  const video = f.jenis === 'video'
  const daftarModel = modelUntukJenis(semua, f.jenis)
  // Teks TIDAK bisa dipilih per konten — `/text/generate` menempuh rantai cadangan tenant
  // dan tidak menerima `model`. Yang paling atas yang dicoba lebih dulu; ditampilkan supaya
  // orang tahu apa yang menulis captionnya, bukan supaya ia memilihnya.
  const modelTeks = semua.filter((m) => m.role === 'text')[0]

  /** Isi SEMUA field dari judul, satu panggilan (ADR-077). */
  async function isiDariJudul() {
    const r = await isi.mutateAsync({ judul: f.judul, jumlah: 1 })
    setPilihanCaption([])
    setPilihDipakai(-1)
    pakai(r?.pilihan?.[0])
  }

  /**
   * Tiga pilihan sekaligus. Kartunya TETAP sesudah dipilih (keputusan operator) supaya
   * bisa dibandingkan bolak-balik tanpa membayar ulang.
   */
  async function isiTigaPilihan() {
    const r = await isi.mutateAsync({ judul: f.judul, jumlah: 3 })
    setPilihanCaption(r?.pilihan ?? [])
    setPilihDipakai(-1)
  }

  /**
   * Terapkan satu pilihan ke ketiga field sekaligus.
   *
   * DIKONFIRMASI kalau caption sudah disunting tangan. Menimpa kalimat yang sudah
   * dirapikan orang tanpa bertanya adalah cara kehilangan pekerjaan yang paling sunyi —
   * dan "sudah disunting" di sini berarti isinya bukan salah satu pilihan yang ditawarkan.
   */
  function pakai(pil, indeks = -1) {
    if (!pil) return
    const adaSuntingan = f.caption.trim()
      && !(pilihanCaption ?? []).some((x) => x.caption === f.caption)
    if (adaSuntingan && !window.confirm(t('bk.timpaCaption'))) return
    setF((x) => ({
      ...x,
      caption: pil.caption || '',
      pesan_kunci: pil.pesan_kunci || x.pesan_kunci,
      hashtag: (pil.hashtag ?? []).map((h) => `#${h}`).join(' ') || x.hashtag,
    }))
    setPilihDipakai(indeks)
  }



  return (
    <div className="flex flex-col gap-3.5">
      {bahan && (
        <Bendera nada="info" judul={t('bk.dariBahan', { judul: bahan.title })}>
          <p>{t('bk.dariBahanIsi', { alasan: bahan.score_reason || '—' })}</p>
        </Bendera>
      )}

      <Bendera nada="info" judul={t('bk.spanduk')}>
        <p>{t('bk.spandukIsi')}</p>
        <p>{t('bk.spandukModel')}</p>
      </Bendera>

      <Kolom2>
        {/* ── Isi konten ───────────────────────────────────────────────── */}
        <Panel judul={t('bk.grupIsi')}>
          <Body>
            <KV>
              <KVBaris label={t('bk.judulInternal')}>
                <Isian
                  value={f.judul}
                  placeholder="Studi kasus klaim 4 jam"
                  onChange={(e) => set('judul', e.target.value)}
                />
                <Sub>{t('bk.judulCatatan')}</Sub>
              </KVBaris>

              <KVBaris label={t('bk.caption')}>
                <AreaTeks
                  value={f.caption}
                  baris={4}
                  placeholder="Berapa lama tim Anda memproses satu klaim?"
                  onChange={(e) => set('caption', e.target.value)}
                />
                {!modelTeks && <Sub>{t('bk.modelTeksKosong')}</Sub>}
              </KVBaris>

              <KVBaris label={t('bk.pesanKunci')}>
                <Isian value={f.pesan_kunci} onChange={(e) => set('pesan_kunci', e.target.value)} />
              </KVBaris>

              <KVBaris label={t('bk.hashtag')}>
                <Isian
                  value={f.hashtag}
                  placeholder="#asuransi #klaimdigital"
                  onChange={(e) => set('hashtag', e.target.value)}
                />
              </KVBaris>
            </KV>

            {/* DUA TOMBOL DI BAWAH KEEMPAT ISIAN, bukan menempel di dalam baris Caption.
                Keduanya mengisi caption, pesan kunci, DAN hashtag sekaligus (ADR-077) —
                jadi menaruhnya di dalam satu baris isian membuatnya terbaca sebagai tombol
                milik baris itu saja. Operator melaporkannya sebagai "urutannya perlu
                diperbaiki". */}
            <div className="mt-3 flex flex-col gap-2">
              <BarisBuat harga={modelTeks ? `${modelTeks.name} · ${t('bk.hargaSetelah')}` : null}>
                  {/* SATU PANGGILAN mengisi caption, pesan kunci, DAN hashtag (ADR-077).
                      Bentuk lama memanggil tiga kali, dan dua yang terakhir disusun dari
                      caption yang sudah jadi — bukan dari gagasan yang sama — jadi ketiganya
                      bisa saling melenceng. */}
                  <TombolBuat
                    disabled={!f.judul.trim() || isi.isPending}
                    onClick={isiDariJudul}
                  >
                    {t('bk.dariJudul')}
                  </TombolBuat>
                  <TombolBuat
                    disabled={!f.judul.trim() || isi.isPending}
                    onClick={isiTigaPilihan}
                  >
                    {t('bk.tigaPilihan')}
                  </TombolBuat>
              </BarisBuat>

              {pilihanCaption.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
                    {t('bk.pilihSatu')}
                  </span>
                  {/* TIGA KOLOM, bukan tiga baris bertumpuk. Ketiganya harus BISA
                      DIBANDINGKAN — itu seluruh gunanya menawarkan tiga — dan pilihan
                      yang harus digulir untuk dilihat bukan pilihan yang dibandingkan.
                      Turun jadi satu kolom di lebar ponsel. */}
                  <div className="grid gap-1.5 sm:grid-cols-3">
                    {pilihanCaption.map((pil, i) => (
                      <button
                        key={i}
                        type="button"
                        aria-pressed={pilihDipakai === i}
                        onClick={() => pakai(pil, i)}
                        className={cx(
                          'text-left bg-surface2 border rounded px-2.5 py-1.5 cursor-pointer',
                          pilihDipakai === i
                            ? 'border-accent text-ink'
                            : 'border-line text-ink2 hover:border-accent hover:text-ink',
                        )}
                      >
                        {/* SUDUTNYA DISEBUT. Tiga kartu tanpa label bedanya adalah tiga
                            kartu yang harus dibaca seluruhnya sebelum bisa dipilih. */}
                        {pil.sudut && (
                          <span className="block text-[10px] font-semibold uppercase
                                           tracking-[.06em] text-ink3">
                            {t(`bk.sudut.${pil.sudut}`, { _: pil.sudut })}
                          </span>
                        )}
                        <span className="block text-[12px]">{pil.caption}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isi.isError && (
              <Bendera nada="bad" role="alert" className="mt-3">{isi.error?.message}</Bendera>
            )}
          </Body>
        </Panel>

        <PanelGambar
          slide={slide}
          setSlide={setSlide}
          daftarModel={daftarModel}
          model={modelPilihan}
          setModel={setModelPilihan}
          video={video}
          referensi={referensi}
          refDipakai={refDipakai}
          temp={temp}
          setTemp={setTemp}
          setRefDipakai={setRefDipakai}
          unggahSlide={async (berkas) => unggahGambar.mutateAsync({ berkas })}
          sedangBuat={gambar.isPending || klipVideo.isPending}
          galat={(video ? klipVideo : gambar).error?.message}
          bisaPrompt={Boolean(f.caption.trim())}
          bahanDiabaikan={bahanDiabaikan}
          dikirimPrompt={dikirimPrompt}
          jenis={f.jenis}
          setJenis={(v) => {
            // PILIHAN MODEL DILEPAS SAAT JENISNYA BERUBAH. Model gambar tidak ada di
            // daftar model video, dan sebaliknya — membiarkannya terbawa menghasilkan
            // 422 dari backend yang menyebut model yang tidak lagi terlihat di layar.
            //
            // Slide dipangkas jadi satu: Instagram tidak punya collase video.
            if (v === f.jenis) return
            setModelPilihan('')
            if (v === 'video') setSlide((x) => x.slice(0, 1))
            set('jenis', v)
          }}
          brief={brief}
          setBrief={setBrief}
          sedangPrompt={promptGambar.isPending}
          buatPrompt={async () => {
            // SATU PANGGILAN untuk SEMUA slide (ADR-077). Slide harus maju berurutan —
            // pengait, isi, penutup — dan `slide` panggilan terpisah menghasilkan `slide`
            // adegan yang tidak berhubungan satu sama lain.
            const r = await promptGambar.mutateAsync({
              caption: f.caption,
              key_message: f.pesan_kunci || '',
              // BRIEF yang paling menentukan, dan boleh kosong.
              brief,
              slide: slide.length,
              // BAHAN YANG DIPILIH ORANG, bentuk sama dengan tombol Buat gambar.
              bahan: bentukBahan(refDipakai, temp),
            })
            const daftar = r?.prompt ?? []
            setSlide((x) => x.map((sl, j) => ({ ...sl, prompt: (daftar[j] || '').trim() })))
            // REFERENSI YANG DIPAKAI MESIN ikut tercentang, dan masih bisa diubah orangnya.
            // Kosong = mesin tidak memakai bahan apa pun; pilihan orang TIDAK ditimpa.
            if (r?.referensi?.length) setRefDipakai(r.referensi)
            setDikirimPrompt(r?.dikirim || null)
            // Bahan yang DIABAIKAN karena model teksnya buta gambar. Dikatakan, bukan
            // didiamkan: promptnya ditulis tanpa melihat bahan yang dipilih orangnya.
            setBahanDiabaikan(Boolean(r?.bahan_diabaikan))
          }}
          buatGambar={async (i, rasio) => {
            // SATU TOMBOL, DUA RUTE. Yang menentukan jenis kontennya, bukan tombol
            // terpisah: dua tombol yang cuma salah satunya pernah berlaku pada satu
            // waktu adalah satu tombol yang salah setengah waktu.
            const r = video
              ? await klipVideo.mutateAsync({
                prompt: slide[i].prompt.trim(),
                model: modelPilihan || null,
              })
              : await gambar.mutateAsync({
                prompt: slide[i].prompt.trim(),
                model: modelPilihan || null,
                bahan: bentukBahan(refDipakai, temp),
                aspect_ratio: rasio,
              })
            // DICATAT, BUKAN DITIMPA — hasil sebelumnya tetap bisa dipilih lagi.
            setSlide((x) => catatHasil(x, i, {
              url: (video ? r?.video_url : r?.image_url) || '',
              tiket: r?.sumber_tiket ?? null,
              biaya: r?.cost_estimate_usd != null
                ? `${r.model_used} · $${r.cost_estimate_usd.toFixed(4)}` : r?.model_used,
            }))
          }}
          ubahGambar={async (i, teks) => {
            // Gambar terpilih + teks Ubah, tidak lebih — lihat `bentukUbah`.
            const r = await ubahGbr.mutateAsync(bentukUbah(slide, i, teks, modelPilihan))
            setSlide((x) => catatHasil(x, i, {
              url: r?.image_url || '',
              tiket: r?.sumber_tiket ?? null,
              biaya: r?.cost_estimate_usd != null
                ? `${r.model_used} · $${r.cost_estimate_usd.toFixed(4)}` : r?.model_used,
            }))
          }}
        />
      </Kolom2>

      {/* ── Kapan & ke mana ────────────────────────────────────────────── */}
      <Panel judul={t('bk.grupKapan')}>
        <Body>
          <KV>
            <KVBaris label={t('bk.tanggalJam')}>
              <Isian
                type="datetime-local"
                className="max-w-[240px]"
                value={f.tayang_pada}
                onChange={(e) => set('tayang_pada', e.target.value)}
              />
              <Sub>{t('bk.tanggalCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('bk.tujuan')}>
              <div className="flex flex-wrap gap-1.5">
                {(tujuan.data ?? []).map((x) => {
                  const aktif = f.tujuan.includes(x.platform)
                  // Label Instagram ikut jenis kontennya: Reels dan Feed adalah dua
                  // tempat berbeda di aplikasi, dan yang memilih perlu tahu yang mana.
                  const label = x.platform === 'instagram'
                    ? (f.jenis === 'video' ? t('bk.igReels') : t('bk.igFeed'))
                    : x.label
                  return (
                    <Tombol
                      key={x.platform}
                      kecil
                      aria-pressed={aktif}
                      jenis={aktif ? 'utama' : 'sekunder'}
                      disabled={!x.siap}
                      title={x.sebab || undefined}
                      onClick={() => set(
                        'tujuan',
                        aktif ? f.tujuan.filter((y) => y !== x.platform) : [...f.tujuan, x.platform],
                      )}
                    >
                      {label}{x.siap ? '' : ` — ${x.sebab}`}
                    </Tombol>
                  )
                })}
              </div>
              {tujuan.isError && (
                <Bendera nada="bad" role="alert" className="mt-2">{tujuan.error?.message}</Bendera>
              )}
              <Sub>{t('bk.tujuanCatatan')}</Sub>
            </KVBaris>
          </KV>
        </Body>

        <Body className="border-t border-line flex flex-wrap gap-2">
          {[[false, 'bk.simpan'], [true, 'bk.simpanDraf']].map(([draf, lbl]) => (
            <Tombol
              key={lbl}
              jenis={draf ? 'sekunder' : 'utama'}
              disabled={!bisa || buat.isPending}
              onClick={() => buat.mutate(
                {
                  judul: f.judul.trim(),
                  jenis: f.jenis,
                  caption: f.caption.trim(),
                  pesan_kunci: f.pesan_kunci.trim() || null,
                  hashtag: f.hashtag.trim() || null,
                  // Slide yang SUDAH punya gambar saja. Yang promptnya terisi tapi
                  // belum dibuat bukan slide — ia niat, dan menerbitkan niat
                  // menghasilkan collase berlubang.
                  media_urls: slide.map((x) => x.url).filter(Boolean),
                  prompt_gambar: slide[0]?.prompt.trim() || null,
                  tayang_pada: f.tayang_pada ? new Date(f.tayang_pada).toISOString() : null,
                  tujuan: f.tujuan,
                  draf,
                },
                {
                  onSuccess: (d) => {
                    setJadi(d)
                    setF(KOSONG)
                    setPilihanCaption([])
                    setSlide([{ prompt: '', url: '', biaya: null }])
                    setRefDipakai([])
                    // Bahan dilepas SESUDAH tersimpan — kalau tidak, spanduk "dari bahan"
                    // tetap menempel di formulir kosong berikutnya dan menyesatkan.
                    bersihkanBahan?.()
                  },
                },
              )}
            >
              {buat.isPending ? t('umum.menyimpan') : t(lbl)}
            </Tombol>
          ))}
        </Body>

        <Body className="border-t border-line flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <b className="text-[11.5px]">{t('bk.alurJudul')}</b>
            <Alur langkah={[
              { id: 'baru', label: t('bk.stBaru') },
              { id: 'scheduled', label: 'scheduled' },
              { id: 'published', label: 'published', akhir: true },
            ]} />
            <span className="text-[11.5px] text-ink3">{t('bk.alurCatatan')}</span>
          </div>
          <p className="m-0 text-[11.5px] text-ink3">{t('bk.alurDraf')}</p>
        </Body>
      </Panel>

      {buat.isError && <Bendera nada="bad" role="alert">{buat.error?.message}</Bendera>}
      {jadi && (
        <Bendera nada="ok" role="status">
          {t(jadi.status === 'draft' ? 'bk.tersimpanDraf' : 'bk.tersimpan',
             { judul: jadi.title, n: jadi.ids.length })}
        </Bendera>
      )}

      <Bendera nada="bad" judul={t('bk.belumJudul')}>
        <p>{t('bk.belumIsi')}</p>
      </Bendera>
    </div>
  )
}
